import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import * as actionTypes from '../../redux/actionTypes';
import RulesTable from './components/RulesTable';
import RuleDialog from './components/RuleDialog';
import RuleFilters from './components/RuleFilters';
import TeamManager from './components/TeamManager';
import { getTeams } from '../../service/dataCategorizationService';
import './RulesManagement.scss';
import { getLdapId } from '../../../../common/Utils/msalUtils';
import useTeamAccess from '../../../../common/hooks/useTeamAccess';

/**
 * RulesManagement Component
 *
 * Main container for managing categorization rules.
 * Provides functionality to create, edit, delete, and toggle rules.
 *
 * @component
 */
const RulesManagement = () => {
    // Get current user LDAP ID (e.g. 'ssin314' from 'ssin314@domain.com')
    const currentUser = getLdapId();
    console.log('[RulesManagement] currentUser:', currentUser);
  const dispatch = useDispatch();
  const toast = useRef(null);

  // Redux state
  const {
    rules,
    rulesLoading,
    error,
    successMessage
  } = useSelector(state => state.dataCategorization);

  // Local state
  const [displayDialog, setDisplayDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [filters, setFilters] = useState({
    team: '',
    enabled: true, // Default to Active
    search: '',
    page: 0,
    size: 20
  });
  const teamsFromStore = useSelector(state => state.dataCategorization.teams);
  const reduxSelectedTeam = useSelector(state => state.dataCategorization.selectedTeam);
  const [selectedTeam, setSelectedTeamLocal] = useState(reduxSelectedTeam || '');
  const setSelectedTeam = useCallback((team) => {
    setSelectedTeamLocal(team);
    dispatch({ type: actionTypes.SET_SELECTED_TEAM, payload: team });
  }, [dispatch]);

  // Sync local team from Redux when switching tabs
  useEffect(() => {
    if (reduxSelectedTeam && reduxSelectedTeam !== selectedTeam) {
      setSelectedTeamLocal(reduxSelectedTeam);
      setFilters(prev => ({
        ...prev,
        team: reduxSelectedTeam,
        enabled: reduxSelectedTeam ? null : true,
        page: 0
      }));
    }
  }, [reduxSelectedTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  const [teamsFromApi, setTeamsFromApi] = useState([]);
  const [teamsApiLoading, setTeamsApiLoading] = useState(true);

  // Team-level access for the currently selected team
  // For rule management: ADMINs only for managed teams, open for unmanaged/legacy teams
  const { isAdmin, hasAccess, isManaged: isTeamManaged, createdBy: teamCreatedBy } = useTeamAccess(selectedTeam);
  // Only ADMINs can manage rules for managed teams; unmanaged teams are open
  const hasTeamAccess = isTeamManaged ? isAdmin : hasAccess;
  const adminContactInfo = teamCreatedBy ? ' (' + teamCreatedBy + ')' : '';

  // Fetch teams directly from the same API as the Excel upload tab
  useEffect(() => {
    setTeamsApiLoading(true);
    getTeams()
      .then(response => {
        const data = response.data || [];
        const mapped = Array.isArray(data)
          ? data.map(team =>
              typeof team === 'string'
                ? { label: team, value: team }
                : { label: team.label || team.name || team.value, value: team.value || team.name || team.label }
            )
          : [];
        setTeamsFromApi(mapped);
      })
      .catch(err => console.error('[RulesManagement] Failed to fetch teams:', err))
      .finally(() => setTeamsApiLoading(false));
  }, []);

  // Merge API teams with any locally-added teams from Redux store
  const teams = useMemo(() => {
    const apiValues = new Set(teamsFromApi.map(t => t.value));
    const storeExtras = (teamsFromStore || [])
      .filter(t => !apiValues.has(t.value));
    return [...teamsFromApi, ...storeExtras];
  }, [teamsFromApi, teamsFromStore]);

  // Load rules on mount and when filters change
  useEffect(() => {
    dispatch({
      type: actionTypes.GET_ALL_RULES,
      payload: filters
    });
  }, [dispatch, filters.team, filters.enabled, filters.search, filters.page, filters.size]);

  // Handle error notifications
  useEffect(() => {
    if (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 5000,
      });
      dispatch({ type: actionTypes.CLEAR_DATA_CATEGORY_ERROR });
    }
  }, [error, dispatch]);

  // Handle success notifications and refresh grid
  useEffect(() => {
    if (successMessage) {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: successMessage,
        life: 3000,
      });

      // Close dialog immediately on success (create/update)
      // For delete, the dialog isn't open so this is a no-op
      setDisplayDialog(false);
      setSelectedRule(null);
      setIsEditMode(false);

      // Refresh rules grid with current filters
      dispatch({
        type: actionTypes.GET_ALL_RULES,
        payload: filters
      });

      const timer = setTimeout(() => {
        dispatch({ type: actionTypes.CLEAR_SUCCESS_MESSAGE });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch, filters]);

  /**
   * Open dialog for creating new rule
   */
  const handleOpenCreateDialog = () => {
    if (!hasTeamAccess) {
      toast.current?.show({ severity: 'warn', summary: 'Access Denied', detail: 'You do not have access to manage rules for this team. Contact the team admin' + adminContactInfo + '.', life: 5000 });
      return;
    }
    setSelectedRule(null);
    setIsEditMode(false);
    setDisplayDialog(true);
  };

  /**
   * Open dialog for editing existing rule
   */
  const handleEditRule = (rule) => {
    if (!hasTeamAccess) {
      toast.current?.show({ severity: 'warn', summary: 'Access Denied', detail: 'You do not have access to manage rules for this team. Contact the team admin' + adminContactInfo + '.', life: 5000 });
      return;
    }
    setSelectedRule(rule);
    setIsEditMode(true);
    setDisplayDialog(true);
  };

  /**
   * Save rule (create or update)
   */
  const handleSaveRule = (ruleData) => {
    if (isEditMode && selectedRule) {
      dispatch({
        type: actionTypes.UPDATE_RULE,
        payload: {
          ruleId: selectedRule.ruleId,
          ...ruleData
        }
      });
    } else {
      dispatch({
        type: actionTypes.CREATE_RULE,
        payload: ruleData
      });
    }
  };

  /**
   * Delete rule with confirmation
   */
  const handleDeleteRule = (ruleId) => {
    if (!hasTeamAccess) {
      toast.current?.show({ severity: 'warn', summary: 'Access Denied', detail: 'You do not have access to manage rules for this team. Contact the team admin' + adminContactInfo + '.', life: 5000 });
      return;
    }
    confirmDialog({
      message: 'Are you sure you want to delete this rule? This action cannot be undone.',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        dispatch({
          type: actionTypes.DELETE_RULE,
          payload: { ruleId }
        });
      },
      reject: () => {}
    });
  };

  /**
   * Toggle rule status (enable/disable)
   */
  const handleToggleStatus = (ruleId, currentStatus) => {
    if (!hasTeamAccess) {
      toast.current?.show({ severity: 'warn', summary: 'Access Denied', detail: 'You do not have access to manage rules for this team. Contact the team admin' + adminContactInfo + '.', life: 5000 });
      return;
    }
    dispatch({
      type: actionTypes.TOGGLE_RULE_STATUS,
      payload: { ruleId, enabled: !currentStatus }
    });
  };

  /**
   * Update filters
   */
  const handleFiltersChange = (newFilters) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters,
      page: 0 // Reset to first page when filters change
    }));
  };

  /**
   * Refresh the rules grid with current filters
   */
  const handleRefreshRules = () => {
    dispatch({
      type: actionTypes.GET_ALL_RULES,
      payload: filters
    });
  };

  /**
   * Close dialog
   */
  const handleCloseDialog = () => {
    setDisplayDialog(false);
    setSelectedRule(null);
    setIsEditMode(false);
  };

  /**
   * Handle team selection from palette
   */
  const handleTeamSelect = useCallback((teamValue) => {
    console.log('[RulesManagement] Team selected:', teamValue);
    setSelectedTeam(teamValue);
    setFilters(prev => ({
      ...prev,
      team: teamValue,
      // Show all rules (active + inactive) for a specific team;
      // revert to active-only when viewing All Teams
      enabled: teamValue ? null : true,
      page: 0
    }));
  }, []);

  return (
    <div className="rules-management-container">
      <Toast ref={toast} />
      <ConfirmDialog />

      <TeamManager />

      <div className="header">
        <div>
          <h2>Rules Management</h2>
          <p className="subtitle">Create and manage categorization rules</p>
        </div>
        {(() => {
          let createRuleTooltip;
          if (selectedTeam !== '' && !hasTeamAccess) {
            createRuleTooltip = 'Access restricted — contact team admin' + adminContactInfo;
          } else {
            createRuleTooltip = undefined;
          }
          return (
            <Button
              label="Create New Rule"
              icon="pi pi-plus"
              className="p-button-primary"
              onClick={handleOpenCreateDialog}
              disabled={selectedTeam !== '' && !hasTeamAccess}
              tooltip={createRuleTooltip}
              tooltipOptions={{ position: 'left' }}
            />
          );
        })()}
      </div>

      {/* Team Palette Selector for Filtering */}
      <div className="team-palette rules-team-palette">
        <span
          className={`team-palette-item${selectedTeam === '' ? ' selected' : ''}`}
          onClick={() => handleTeamSelect('')}
          style={{ cursor: 'pointer' }}
        >
          All Teams
        </span>
        {teamsApiLoading && teams.length === 0 && (
          <span style={{ color: '#6c757d', fontSize: '13px', alignSelf: 'center' }}>
            <i className="pi pi-spin pi-spinner" style={{ marginRight: 6 }} />
            Loading teams...
          </span>
        )}
        {!teamsApiLoading && teams.length === 0 && (
          <span style={{ color: '#6c757d', fontSize: '13px', alignSelf: 'center' }}>
            No teams available
          </span>
        )}
        {teams.map(team => (
          <span
            key={team.value}
            className={`team-palette-item${selectedTeam === team.value ? ' selected' : ''}`}
            onClick={() => handleTeamSelect(team.value)}
            style={{ cursor: 'pointer' }}
          >
            {team.label}
          </span>
        ))}
      </div>

      {/* Access restriction notice */}
      {selectedTeam && isTeamManaged && !hasTeamAccess && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: '1rem', fontSize: '0.88rem', color: '#b91c1c' }}>
          <i className="pi pi-lock" style={{ fontSize: '1rem' }} />
          <span>
            You don’t have access to manage rules for <strong>{selectedTeam}</strong>.
            {teamCreatedBy && <> Contact the team admin: <strong>{teamCreatedBy}</strong>.</>}
          </span>
        </div>
      )}

      <RuleFilters
        filters={filters}
        onFilterChange={handleFiltersChange}
        onRefresh={handleRefreshRules}
        hideTeamFilter
      />

      <RulesTable
        rules={rules || []}
        loading={rulesLoading}
        onEdit={handleEditRule}
        onDelete={handleDeleteRule}
        onToggleStatus={handleToggleStatus}
      />

      <RuleDialog
        visible={displayDialog}
        isEditMode={isEditMode}
        rule={selectedRule}
        onHide={handleCloseDialog}
        onSave={handleSaveRule}
        loading={rulesLoading}
        teams={teams}
      />
    </div>
  );
};

export default RulesManagement;

